/**
 * Zone 0 — 3-second status summary banner.
 * A single-line bar at the top of the Dashboard showing at-a-glance health.
 */

import { t } from "../i18n";

type StatusBarProps = {
  readonly alertCount: number;
  readonly anomalousKpiCount: number;
  readonly normalKpiCount: number;
  readonly onClick?: () => void;
};

type Severity = "critical" | "warning" | "healthy";

function resolveSeverity(
  alertCount: number,
  anomalousKpiCount: number,
): Severity {
  if (alertCount > 0) return "critical";
  if (anomalousKpiCount > 0) return "warning";
  return "healthy";
}

function Dot({ severity }: { readonly severity: Severity }): React.JSX.Element {
  const bg =
    severity === "critical"
      ? "#dc2626"
      : severity === "warning"
        ? "#f59e0b"
        : "#16a34a";
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: bg,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}

function buildMessage(
  severity: Severity,
  alertCount: number,
  anomalousKpiCount: number,
  normalKpiCount: number,
): React.JSX.Element {
  const alerts = t("dashboard.status_alerts", { count: alertCount });
  const anomalous = t("dashboard.status_anomalous", {
    count: anomalousKpiCount,
  });
  const normal = t("dashboard.status_normal", { count: normalKpiCount });
  const allHealthy = t("dashboard.status_all_healthy");

  if (severity === "critical") {
    return (
      <span style={s.row}>
        <Dot severity="critical" />
        {alerts}
        <span style={s.separator}>·</span>
        <Dot severity="warning" />
        {anomalous}
        <span style={s.separator}>·</span>
        <Dot severity="healthy" />
        {normal}
      </span>
    );
  }

  if (severity === "warning") {
    return (
      <span style={s.row}>
        <Dot severity="warning" />
        {anomalous}
        <span style={s.separator}>·</span>
        <Dot severity="healthy" />
        {normal}
        <span style={s.separator}>·</span>
        {t("dashboard.status_alerts", { count: 0 })}
      </span>
    );
  }

  return (
    <span style={s.row}>
      <Dot severity="healthy" />
      {allHealthy}
      <span style={s.separator}>·</span>
      {t("dashboard.status_alerts", { count: 0 })}
      <span style={s.separator}>·</span>
      {normal}
    </span>
  );
}

const THEME: Record<Severity, { bg: string; border: string }> = {
  critical: { bg: "#fef2f2", border: "#fecaca" },
  warning: { bg: "#fef9c3", border: "#fde68a" },
  healthy: { bg: "#dcfce7", border: "#bbf7d0" },
};

export function StatusBar({
  alertCount,
  anomalousKpiCount,
  normalKpiCount,
  onClick,
}: StatusBarProps): React.JSX.Element {
  const severity = resolveSeverity(alertCount, anomalousKpiCount);
  const theme = THEME[severity];
  const clickable = typeof onClick === "function";

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") onClick();
            }
          : undefined
      }
      style={{
        ...s.bar,
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        cursor: clickable ? "pointer" : "default",
      }}
    >
      {buildMessage(severity, alertCount, anomalousKpiCount, normalKpiCount)}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  bar: {
    padding: "12px 20px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
  },
  row: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  separator: {
    color: "var(--color-text-secondary)",
    fontWeight: 400,
  },
};
